import { type HTMLAttributes, type ReactNode } from 'react';
import { Crown } from 'lucide-react';
import { cn, roleBadgeVariants, type RoleBadgeVariants, type SystemRole } from '../../../utils';

export type { SystemRole };

export interface RoleBadgeProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, 'role'>,
    RoleBadgeVariants {
  /** The system role to display */
  role: SystemRole;
  /** Whether to show role-specific icon (e.g., crown for owner) */
  showIcon?: boolean;
  /** Custom label to display (if not provided, role name is capitalized) */
  label?: string;
  /** Custom icon to display instead of default */
  icon?: ReactNode;
}

/**
 * RoleBadge - Displays user roles with consistent Agion brand styling.
 *
 * Supports the canonical roles: owner, admin, manager, user, auditor, and
 * guest (an external member, ADR-20260930)
 *
 * Features:
 * - Color-coded by role for quick visual identification
 * - Two emphasis levels: default (subtle) and strong (more saturated)
 * - Optional role-specific icons (crown for owner by default)
 * - Full dark mode support
 */
export function RoleBadge({
  className,
  role,
  emphasis,
  size,
  showIcon = false,
  label,
  icon,
  ...props
}: RoleBadgeProps) {
  // Default to capitalized role name
  const displayLabel = label || role.charAt(0).toUpperCase() + role.slice(1);

  // Determine which icon to show
  const IconComponent = () => {
    if (icon) {
      return <>{icon}</>;
    }
    if (showIcon && role === 'owner') {
      return <Crown className={cn('flex-shrink-0', size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5')} aria-hidden="true" />;
    }
    return null;
  };

  return (
    <span
      className={cn(roleBadgeVariants({ role, emphasis, size }), className)}
      {...props}
    >
      <IconComponent />
      {displayLabel}
    </span>
  );
}
